/**
 * Update Study Structure Dialog - Main Wizard Controller
 *
 * Migrated from: lego/page/etmf/overview/update-study/Dialog.jsx
 */

import React, { useState } from 'react'
import Modal from '../../../../components/Modal'
import { Formik, FormikHelpers } from 'formik'
import { DialogState } from './context/DialogState'
import { useDialogContext } from './context/useDialogContext'
import { ValidationSchema } from './Validation'
import Step1 from './Step1'
import Step2 from './Step2'
import Step3 from './Step3'
import LoadingSpinner from '../../../../components/LoadingSpinner'
import { useServiceContext } from '../../../../root'
import type { FormValues } from './context/DialogContext'

interface UpdateStudyDialogProps {
  onClose: () => void
}

function DialogContent({ onClose, onTitleChange }: { onClose: () => void; onTitleChange: (title: string) => void }) {
  const context = useServiceContext()
  const dialogData = useDialogContext()
  const { translate } = context.service.filter

  // Update modal title whenever it changes
  React.useEffect(() => {
    onTitleChange(dialogData.title)
  }, [dialogData.title, onTitleChange])

  const handleSubmit = async (values: FormValues, formikBag: FormikHelpers<FormValues>) => {
    if (dialogData.currentStep === 3) {
      // Update values.current before submitting
      dialogData.setCurrentValues(values, formikBag.setValues)
      await dialogData.submit(values)
      // Note: submit() calls closeModal() on success, so we don't call onClose() here
    } else {
      // Reset touched for next step
      const touchReset = Object.keys(dialogData.getDefaultValues()).reduce(
        (acc, key) => ({ ...acc, [key]: false }),
        {} as Record<string, boolean>
      )
      formikBag.setTouched(touchReset, false)

      dialogData.goToNextStep(values)
    }
  }

  const validate = async (values: FormValues) => {
    try {
      await ValidationSchema(dialogData.currentStep).validate(values, {
        abortEarly: false,
        context: values
      })
      return {}
    } catch (err: any) {
      console.error('Validation failed:', err)
      const errors: Record<string, string> = {}
      if (err.inner) {
        err.inner.forEach((error: any) => {
          if (error.path) {
            // Translate the error message
            errors[error.path] = translate(error.message)
          }
        })
      }
      return errors
    }
  }

  return (
    <Formik
      validate={validate}
      onSubmit={handleSubmit}
      initialValues={dialogData.getDefaultValues()}
      enableReinitialize={false}
    >
      {({ values, setFieldValue, setValues, submitForm }) => (
        <>
          <Modal.Body>
            {dialogData.isLoading && <LoadingSpinner centered size={40} />}
            {!dialogData.isLoading && (
              <>
                {dialogData.currentStep === 1 && <Step1 values={values} setFieldValue={setFieldValue} />}
                {dialogData.currentStep === 2 && <Step2 values={values} setFieldValue={setFieldValue} />}
                {dialogData.currentStep === 3 && (
                  <Step3 values={values} setFieldValue={setFieldValue} setValues={setValues} />
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-link" onClick={onClose} disabled={dialogData.isLoading}>
              {translate('CANCEL')}
            </button>
            <button
              type="submit"
              className="btn btn-primary text-capitalize"
              onClick={submitForm}
              disabled={dialogData.isLoading}
            >
              {translate(dialogData.currentStep < 3 ? 'ETMF_GENERATE_TRIAL_STRCT_BTN_NEXT' : 'SAVE')}
            </button>
          </Modal.Footer>
        </>
      )}
    </Formik>
  )
}

export default function UpdateStudyDialog({ onClose }: UpdateStudyDialogProps) {
  const [modalTitle, setModalTitle] = useState('Update Study Structure')

  return (
    <Modal show={true} handleClose={onClose} title={modalTitle} size="md">
      <DialogState closeModal={onClose}>
        <DialogContent onClose={onClose} onTitleChange={setModalTitle} />
      </DialogState>
    </Modal>
  )
}
